import type { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import toast from 'react-hot-toast'
import type { Game } from '@/redux/reducers/gameReducer'
import ThaiLottoCard, {
  type ThaiLottoCardProps,
} from '@/ui-components/components/ThaiLottoCard'
import { DATE_FORMAT_HUMAN } from '@/utils/date-time-utils'
import { getLottoNameAndCountryFromLottoGame } from '@/utils/lotto-type-v2'
import { getBackgroundFlagGroup } from '@/utils/flag-utils'
import {
  createSection,
  formatDateTime,
  getResultNumber,
} from './result-card.utils'
import { trans } from '@/i18n/utils'

const buildThaiLottoSections = (
  round: Game['rounds'][0],
  t: TFunction,
): ThaiLottoCardProps['sections'] => {
  const sections: ThaiLottoCardProps['sections'] = []
  const noRoundMessage = t('message.no_round') || 'No Round'

  if (!round) {
    return [
      {
        title: t('betDetailPanel.betLottoType.bet_three_top'),
        numbers: [noRoundMessage],
      },
      {
        title: t('betDetailPanel.betLottoType.bet_two_under'),
        numbers: [noRoundMessage],
      },
      {
        title: t('betDetailPanel.betLottoType.bet_three_front'),
        numbers: [noRoundMessage],
      },
      {
        title: t('betDetailPanel.betLottoType.bet_three_under'),
        numbers: [noRoundMessage],
      },
      {
        title: t('betDetailPanel.betLottoType.bet_two_top'),
        numbers: [noRoundMessage],
      },
    ]
  }

  // Three Top
  if (round.top_three_result) {
    sections.push(
      createSection(
        t('betDetailPanel.betLottoType.bet_three_top'),
        getResultNumber(round.top_three_result),
      ),
    )
  }

  // Two Under
  if (round.bottom_two_result) {
    sections.push(
      createSection(
        t('betDetailPanel.betLottoType.bet_two_under'),
        getResultNumber(round.bottom_two_result),
      ),
    )
  }

  // Three Front
  if (round.bottom_three_front_1_result || round.bottom_three_front_2_result) {
    const threeFrontNumbers = []
    if (round.bottom_three_front_1_result) {
      threeFrontNumbers.push(getResultNumber(round.bottom_three_front_1_result))
    }
    if (round.bottom_three_front_2_result) {
      threeFrontNumbers.push(getResultNumber(round.bottom_three_front_2_result))
    }
    sections.push(
      createSection(
        t('betDetailPanel.betLottoType.bet_three_front'),
        threeFrontNumbers,
      ),
    )
  }

  // Three Bottom
  if (round.bottom_three_1_result || round.bottom_three_2_result) {
    const threeBottomNumbers = []
    if (round.bottom_three_1_result) {
      threeBottomNumbers.push(getResultNumber(round.bottom_three_1_result))
    }
    if (round.bottom_three_2_result) {
      threeBottomNumbers.push(getResultNumber(round.bottom_three_2_result))
    }
    sections.push(
      createSection(
        t('betDetailPanel.betLottoType.bet_three_under'),
        threeBottomNumbers,
      ),
    )
  }

  // Two Top
  if (round.result_top_two) {
    sections.push(
      createSection(
        t('betDetailPanel.betLottoType.bet_two_top'),
        getResultNumber(round.result_top_two),
      ),
    )
  }

  if (!round?.is_result_confirmed) {
    return sections.map((i) => ({
      ...i,
      result: t('betResult.waiting'),
    }))
  }

  return sections
}

export const ThaiLottoResultCard = ({
  game,
  rounds,
}: {
  game: Game
  rounds: Game['rounds']
}) => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()

  const { country, name: flagBackgroundName } =
    getLottoNameAndCountryFromLottoGame(game.type, game.flag)
  const round = rounds?.[0]
  const title = trans(i18n, game.translations, 'name')
  const closeAt = round?.close_at

  const onClick = () => {
    if (round?.id) {
      navigate(`/lotto/${game.code}/${game.id}/${round.id}`)
    } else {
      toast.error(t('message.no_round'))
    }
  }

  return (
    <ThaiLottoCard
      key={`thai-lotto-${game.id}`}
      onClick={onClick}
      country={country}
      flagBackgroundName={getBackgroundFlagGroup(flagBackgroundName)}
      title={title}
      sections={buildThaiLottoSections(round, t)}
      noRound={!round}
      date={formatDateTime(DATE_FORMAT_HUMAN, closeAt, i18n.language)}
      time={formatDateTime('HH:mm', closeAt, i18n.language)}
    />
  )
}
